import React, { useState, useRef, useEffect } from 'react';
import './index.scss';
import SvgIcon from '@home/components/SvgIcon';
import RenameIcon from './svg/rename.svg';
import ArchiveIcon from './svg/star.svg';
import RemoveArchiveIcon from './svg/star-cancel.svg';
import TrashIcon from './svg/trashbin.svg';
import classNames from 'classnames';
import { useHistory, useIntl } from 'umi';
import { Popover, DropdownBox, List, Icon } from 'tea-component';
import CommonBubble from '@home/components/CommonBubble';
import { useDebounceFn } from 'ahooks';
import { buriedBtnPoint } from '@home/utils/buriedPoint';
import useChatShareHooks from '@/pages/aigc/pages/Agent/ChatShare/useChatShareHooks';
import { ChatShareDialog } from '@/pages/aigc/pages/Agent/ChatShare/types/chatShare';
import { SearchSessionQueryResponse } from '@/pages/aigc/pages/Agent/service/service';
import { findMostOccurrences, getExpectedTemplate } from '@/pages/aigc/utils/utils';
import { CHAT_DS_TEMPLATES } from '@/pages/aigc/pages/Agent/ChatDSTemplate/constants';
import type { TChartTemplate } from '@/pages/aigc/pages/Agent/ChatDSTemplate/type';
import { highlightText } from '@/pages/aigc/pages/Agent/components/Tabs/utils/highlight';
import { useChatList } from '@/pages/aigc/layouts/agentLayout/hooks';
import { useSearchParamsAppoint, useUrlQueryParam } from '@/pages/aigc/hooks/useQuery';
import { useLocalState } from '../../ChatPage/hooks/useLocalState';

interface TabsProps {
  title: string;
  id: string;
  active: boolean;
  setShowShareChatDialog: React.Dispatch<React.SetStateAction<ChatShareDialog | undefined>>;
  hasSensitiveContent?: boolean;
  operation?: boolean;
  isLoading?: boolean;
  isBookmarkItem?: boolean;
  onEdit?: (id: string, newTitle: string, callback: () => void, isBookmarked: boolean) => void;
  onDelete?: (id: string) => void;
  onBookmark?: (id: string, isRemoveArchive: boolean) => void;
  onMouseEnter?: (id: string) => void;
  isInBookmark?: boolean;
  searchQuery?: {
    searchKeyword: SearchSessionQueryResponse['chats'][0]['keyword'];
    searchQuery: SearchSessionQueryResponse['chats'][0]['messages'];
  };
}

const Tabs = (props: TabsProps) => {
  const {
    hasSensitiveContent = false,
    title,
    id,
    onEdit,
    onDelete,
    onBookmark,
    active,
    operation = true,
    isLoading,
    isBookmarkItem = false,
    onMouseEnter,
    setShowShareChatDialog,
    isInBookmark = false,
    searchQuery,
  } = props;
  // 是否显示编辑 & 删除菜单
  const [showMenu, setShowMenu] = useState(false);

  // 编辑
  const editInputRef = useRef<HTMLInputElement>(null);
  const tabsTitleRef = useRef<HTMLDivElement>(null);

  const [isEditing, setIsEditing] = useState(false);
  const [editedTitle, setEditedTitle] = useState(title);
  const [isTitleChanged, setIsTitleChanged] = useState(false);
  const [isHoverShareButton, setIsHoverShareButton] = useState(false); // 临时更改

  const history = useHistory();
  const { formatMessage } = useIntl();

  const { shareOnClickHandler } = useChatShareHooks();
  const { chatList, bookMarkedChats } = useChatList({ manual: true });
  const { template, sessionId } = useSearchParamsAppoint();
  // 这个hook用于在url，新增sessionId，不破坏各系统路由原有的参数
  const [, setInitialTemp] = useUrlQueryParam(['template']);
  // 全局会话模式状态：标识当前会话属于什么模式（如 skill / dsReport / aiExcel 等），供 Copilot 等无 URL template 的场景消费
  const [, setSessionTemplateMode] = useLocalState<string>('__dba_session_template_mode__', '');
  const filteredChatList = isInBookmark ? bookMarkedChats : chatList;
  // TODO: 这里后端字段还没给出,先这样定义
  const matchModeItem = filteredChatList.find((item) => item.session_id === id);

  const expectedTemplate = getExpectedTemplate(matchModeItem);
  // 当前会话项是否已在列表中匹配到：用于区分「数据未加载」与「普通 aiAgent 会话」
  // 两种 expectedTemplate 同样为空的情况，避免数据未加载时误清特殊会话模式
  const hasMatchModeItem = !!matchModeItem;
  useEffect(() => {
    if (sessionId !== id) return;
    // 数据未加载（列表中尚未匹配到当前会话）时，不做任何修正
    if (!hasMatchModeItem) return;
    // URL 上的 template 与当前会话类型不一致时，自动修正（仅修正真正的布局模板；
    // knowledge 等会话属性不占 URL 的 template 参数，由下方全局会话模式状态兜底消费）
    if (
      expectedTemplate
      && expectedTemplate !== (template || '')
      && CHAT_DS_TEMPLATES.includes(expectedTemplate as TChartTemplate)
    ) {
      setInitialTemp({ template: expectedTemplate });
    }
    // 同步写入全局会话模式状态：普通会话（expectedTemplate 为空）也写入 ''，
    // 主动清除上一个特殊会话（dsReport / skill 等）残留的全局会话模式，
    // 避免污染 Copilot 等共享该状态的场景，导致 Skill / 图表按钮偶发不显示
    setSessionTemplateMode(expectedTemplate ?? '');
    return () => {
      setSessionTemplateMode('');
    };
  }, [expectedTemplate, hasMatchModeItem, id, sessionId, template]);

  useEffect(() => {
    setIsTitleChanged(true);
    setEditedTitle(title); // 当外部 title 变化时，同步本地状态
    setIsTitleChanged(false);
  }, [title]);

  const startEditing = (e: any) => {
    e.stopPropagation();
    setIsEditing(true);
    setShowMenu(false);
    setTimeout(() => editInputRef.current?.focus(), 0);
  };

  const resetEditState = () => {
    setIsTitleChanged(false);
    setEditedTitle(title);
  };

  const saveEdit = () => {
    if (editedTitle.trim() && editedTitle !== title) {
      setIsTitleChanged(true);
      onEdit(id, editedTitle.trim(), resetEditState, isBookmarkItem);
    } else {
      setEditedTitle(title); // 恢复原值
    }
    setIsEditing(false);
  };

  const handleDelete = (e: any) => {
    e.stopPropagation();
    onDelete(id);
    setShowMenu(false);
  };

  const handleBookmark = (e: any) => {
    if(!onBookmark) return;
    e.stopPropagation();

    if(!isBookmarkItem) onBookmark(id, false);
    else onBookmark(id, true);

    setShowMenu(false);
  };

  const handleClickChat = () => {
    // 仅携带真正的布局模板；knowledge 等会话属性走全局会话模式状态，不写 URL template
    const templateParam =
      expectedTemplate && CHAT_DS_TEMPLATES.includes(expectedTemplate as TChartTemplate)
        ? `&template=${expectedTemplate}`
        : '';
    history.push(`/agent/chat?sessionId=${id}${templateParam}`);
  };

  const checkTextOverflow = (element: HTMLElement | null): boolean => {
    if (!element) return false;
    return element.scrollWidth > element.clientWidth;
  };

  // onMouseEnter 事件处理
  const { run: handleOnMouseEnter } = useDebounceFn((e) => {
    const isClipped = checkTextOverflow(tabsTitleRef?.current);
    onMouseEnter?.(!isClipped ? '' : e);
  }, { wait: 100, });

  const menuId = isInBookmark ? `bookmark-${id}` : id;
  const allQuestion = (searchQuery?.searchQuery && searchQuery?.searchQuery.length > 0) ? searchQuery?.searchQuery.reduce<string[]>((acc, question) => {
    acc.push(question.query.parts[0].text);
    return acc;
  }, []) : [];

  const highlightQuestion = (findMostOccurrences(allQuestion, searchQuery?.searchKeyword ?? ''));
 
  return (
    <div
      className={classNames(
        searchQuery?.searchQuery && 'dba-seach-tab',
        'dba-qa-tab',
        active && 'dba-qa-tab-active',
        (isLoading || isTitleChanged) && 'dba-qa-loading',
      )}
      onClick={handleClickChat}
      onMouseEnter={() => handleOnMouseEnter(menuId)}
      onMouseLeave={() => handleOnMouseEnter?.('')}
      id={menuId}
    >
      {(() => {
        if (isLoading || isTitleChanged) {
          return (
            <>
              <Icon type="loading"></Icon>
              <span>{isTitleChanged ? formatMessage({ id: 'dba_menu_rename' }) : formatMessage({ id: 'dba_menu_loading' })}</span>
            </>
          );
        }
        if (isEditing) {
          return (
            <input
              ref={editInputRef}
              className="dba-edit-input"
              value={editedTitle}
              onChange={(e) => setEditedTitle(e.target.value)}
              onBlur={saveEdit}
              onKeyDown={(e) => {
                e.key === 'Enter' && saveEdit();
              }}
              onClick={(e) => {
                e.stopPropagation();
              }}
            />
          );
        }
        return (
          <>
            {searchQuery?.searchKeyword === undefined && <span ref={tabsTitleRef} className="dba-tabs-title">
              {title}
            </span>}

            {searchQuery?.searchKeyword && 
              <div className={'dba-tabs-title-wrapper'}>
                <span 
                  ref={tabsTitleRef} 
                  className="dba-tabs-title"   
                >{highlightText(title, searchQuery.searchKeyword)}</span>

                <span className="dba-tabs-title dba-search-query">
                  {highlightText(highlightQuestion.text, searchQuery.searchKeyword)}
                </span> 
              </div>
            }

            {operation && (
              <Popover
                trigger="click"
                placement="bottom-start"
                placementOffset={[0, 6]}
                visible={showMenu}
                onVisibleChange={(visible) => {
                  setShowMenu(visible);
                  if (visible) {
                    onMouseEnter?.('');
                  }
                }}
                overlayClassName="dba-tabs-dropdown-container"
                overlay={
                  <DropdownBox className="dba-tabs-dropdown">
                    <List type="option">
                      <CommonBubble
                        className='dba-tabs-dropdown-share-button-bubble'
                        placement="right"
                        closeOnScroll
                        destroyOnClose
                        visible={hasSensitiveContent && isHoverShareButton}
                        // onVisibleChange={(bool)=> {
                        //   if(bool) setTabBubbleVisible(false);
                        // }}
                        overlayClassName='dba-nav-menu-overlay'
                        content={formatMessage({ id: 'dba_menu_sensitive_content' })}
                      >
                        <List.Item
                          disabled={hasSensitiveContent}
                          className="dba-tabs-option-item-wrapper"
                          onClick={(e) => {
                            e.stopPropagation();
                            buriedBtnPoint({
                              pageId: '700501',
                              buttonId: '700501008',
                              extInfo: JSON.stringify({
                                sessionId: id,
                                from: 'leftMenu',
                              }),
                              typeId: 'aigc',
                            });
                            setShowMenu(false);
                            shareOnClickHandler(id, setShowShareChatDialog);
                          }}

                        >
                          <div onMouseEnter={() => setIsHoverShareButton(true)} onMouseLeave={() => setIsHoverShareButton(false)}>
                            <SvgIcon type="link-new"></SvgIcon>
                            <span className={classNames("dba-tabs-option-item-title", {
                              "dba-tabs-option-item-title-disabled": hasSensitiveContent
                            })} data-disabled={hasSensitiveContent}>
                              {formatMessage({ id: 'dba_menu_share' })}
                            </span>
                          </div>
                        </List.Item>
                      </CommonBubble>

                      <hr />

                      <List.Item
                        className="dba-tabs-option-item-wrapper"
                        onClick={startEditing}
                      >
                        {/* <SvgIcon type="edit-icon"></SvgIcon> */}
                        <img src={RenameIcon} alt="icon" className='dba-tabs-option-item-icon' />
                        <span className="dba-tabs-option-item-title">
                          {formatMessage({ id: 'dba_menu_rename' })}
                        </span>
                      </List.Item>

                      <List.Item
                        className="dba-tabs-option-item-wrapper"
                        onClick={handleBookmark}
                      >
                        {/* <SvgIcon type="edit-icon"></SvgIcon> */}
                        <img src={isBookmarkItem ? RemoveArchiveIcon : ArchiveIcon} alt="icon" className='dba-tabs-option-item-icon' />
                        <span className="dba-tabs-option-item-title">
                          {isBookmarkItem ? formatMessage({ id: 'dba_menu_chat_archive_remove' }) : formatMessage({ id: 'dba_menu_chat_archive' })}
                        </span>
                      </List.Item>
                      
                      <List.Item
                        className="dba-tabs-option-item-wrapper"
                        onClick={handleDelete}
                      >
                        {/* <SvgIcon type="trash-can-icon"></SvgIcon> */}
                        <img src={TrashIcon} alt="icon" className='dba-tabs-option-item-icon' />
                        <span className="dba-tabs-option-item-title">
                          {formatMessage({ id: 'aigc_common_delete' })}
                        </span>
                      </List.Item>
                    </List>
                  </DropdownBox>
                }
              >
                <SvgIcon
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowMenu(!showMenu);
                  }}
                  className="dba-tabs-options"
                  type="there-points"
                ></SvgIcon>
              </Popover>
            )}
          </>
        );
      })()}
    </div>
  );
};

export default Tabs;
